import type { Currency } from '@btsis/core';
import { compareTurkish, lowerTurkish, upperTurkish } from '@btsis/core';

const LOCALE = 'tr-TR';

const numberFormatter = new Intl.NumberFormat(LOCALE);

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: Currency): Intl.NumberFormat {
  let formatter = currencyFormatters.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat(LOCALE, { style: 'currency', currency });
    currencyFormatters.set(currency, formatter);
  }
  return formatter;
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatMoney(amountMinor: string | bigint, currency: Currency): string {
  const minor = BigInt(amountMinor);
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const major = Number(abs / 100n) + Number(abs % 100n) / 100;
  return currencyFormatter(currency).format(negative ? -major : major);
}

const POSSESSIVE_BY_DIGIT = ['', "'i", "'si", "'ü", "'ü", "'i", "'sı", "'si", "'i", "'u"];
const POSSESSIVE_BY_TEN = ['', "'u", "'si", "'u", "'ı", "'si", "'ı", "'i", "'i", "'ı"];

/**
 * A count with the third-person possessive suffix Turkish gives it —
 * *3 hayvandan 1'i*, *2'si*, *3'ü* — chosen by how the number is read aloud:
 * its last digit, else its tens, else *yüz*, *bin* or *milyon*.
 */
export function formatCountPossessive(value: number): string {
  const whole = Math.trunc(Math.abs(value));
  const digit = whole % 10;
  const ten = Math.trunc(whole / 10) % 10;
  let suffix: string;
  if (whole === 0) suffix = "'ı";
  else if (digit !== 0) suffix = POSSESSIVE_BY_DIGIT[digit] as string;
  else if (ten !== 0) suffix = POSSESSIVE_BY_TEN[ten] as string;
  else if (whole % 1000 !== 0) suffix = "'ü";
  else if (whole % 1_000_000 !== 0) suffix = "'i";
  else suffix = "'u";
  return `${formatNumber(value)}${suffix}`;
}

const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/** A byte count as a person reads it, in the Turkish locale: `1536` reads `1,5 KB`. */
export function formatFileSize(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : 1;
  const text = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: digits }).format(value);
  return `${text} ${FILE_SIZE_UNITS[unit]}`;
}

const percentFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  maximumFractionDigits: 1,
});

/** A ratio as a Turkish percentage: `1.045` reads as `%104,5`. */
export function formatPercent(ratio: number): string {
  return percentFormatter.format(ratio);
}

export function formatDate(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium' }).format(date);
}

export function formatDateTime(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

// Turkish has dotted and dotless i, so a naive toLowerCase/localeCompare
// mis-sorts and mis-matches text. The fold lives in @btsis/core because the
// API normalizes donor names with the same one and cannot import this package.
export function compareTr(a: string, b: string): number {
  return compareTurkish(a, b);
}

export function lowerTr(value: string): string {
  return lowerTurkish(value);
}

export function upperTr(value: string): string {
  return upperTurkish(value);
}

export * from './csv.js';
